/**
 * Web Locks API (`navigator.locks`) によるリーダー選出 flush 直列化 (docs/03 §7.5-3 層1)。
 *
 * 複数タブ / 複数 hook インスタンスが同時に outbox を flush すると、同一 doc への冗長な setDoc /
 * getDoc / rules 評価が N 並列で走る (冪等なので**結果は壊れない**が無駄打ち)。これを防ぐため、
 * flush 本体を名前付きロックでラップし「最初にロックを取れたタブだけが代表で送る」。
 *
 * **重要 (docs/03 §7.5-3)**: Web Locks は「無駄打ち防止の最適化」であって安全性の必要条件ではない。
 * 安全性 (消失・重複なし) は **決定的 clientId による冪等送信**が担保する。よって:
 *   - `navigator.locks` 非対応 (secure context でない / 旧 WebView) → ロック無しで実行 (各タブが送るが冪等で無害)。
 *   - ロックを他タブが保持中 (`ifAvailable: true` で取得失敗) → `FLUSH_SKIPPED` を返して即降りる
 *     (代表タブに任せる。取りこぼしても次回 online / 変化 / 起動で再 flush される)。
 *
 * secure context 要件: `navigator.locks` は HTTPS / localhost でのみ利用可。本番 (HTTPS) は満たす。
 */

/** ロックを取得できず flush を skip したことを示す sentinel。 */
export const FLUSH_SKIPPED = Symbol('flush-skipped');

/** `navigator.locks` が利用可能か (secure context + 対応ブラウザ)。 */
export function isFlushLockSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.locks !== 'undefined' &&
    typeof navigator.locks.request === 'function'
  );
}

/**
 * `fn` を名前付きロック下で実行する。
 *
 * @returns `fn` の戻り値。ロックを他タブが保持中で取得できなかった場合は `FLUSH_SKIPPED`。
 *          Web Locks 非対応環境ではロック無しでそのまま `fn` を実行しその戻り値を返す。
 */
export async function withFlushLock<T>(
  name: string,
  fn: () => Promise<T>,
): Promise<T | typeof FLUSH_SKIPPED> {
  if (!isFlushLockSupported()) {
    // 非対応: ロック無しで実行 (冪等送信が安全性を担保するので無害)。
    return await fn();
  }
  return await navigator.locks.request(name, { ifAvailable: true }, async (lock) => {
    // lock === null は「他タブが保持中で取得できなかった」(ifAvailable: true)。
    if (!lock) return FLUSH_SKIPPED;
    return await fn();
  });
}
